/**
 * AuthVerifyPage — handles the magic-link arrival path.
 *
 * The email contains a "Sign in" button that lands here at
 *   /auth/verify?token=<64-hex-token>
 *
 * Flow:
 *   1. Read ?token= from URL + email_login_session_id from localStorage.
 *   2. Call verifyEmailLoginToken CF.
 *   3a. matchedDevice: true  → signInWithCustomToken, localStorage setup,
 *                              role-based redirect (mirrors LandingPage.finalizeSignIn).
 *   3b. matchedDevice: false → cross-device explainer with masked email +
 *                              "go back to your original device and use the code".
 *   3c. error                → friendly message + link back to /.
 *
 * No auth required — lives outside AuthGuard.
 */

import { useEffect, useRef, useState } from 'react';
import { setSessionIdentity, sessionRoleForUserRole } from '@/lib/session';
import { clearPendingEmailLogin } from '@/lib/pending-email-login';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { Loader2, MailCheck, AlertTriangle, ShieldCheck } from 'lucide-react';
import { auth, functions } from '@/firebase';
import { setSessionExpiry } from '@/hooks/useAuth';
import { setAuthExpiry } from '@/hooks/useTechAuth';

type VerifyStatus = 'verifying' | 'cross-device' | 'error';

interface VerifiedUser {
  id: string;
  name: string;
  email: string;
  role: string;
  tenantId: string;
  dualAccess?: boolean;
}

type VerifyResponse =
  | {
      matchedDevice: true;
      customToken: string;
      user: VerifiedUser;
      emailVerifiedUntil: string;
    }
  | { matchedDevice: false; maskedEmail: string };

/** Map CF HttpsError codes to friendly messages — mirrors the shape in
 *  LandingPage.mapEmailErrorCode but only the cases we expect here. */
function mapVerifyError(err: unknown): string {
  const e = err as { code?: string; message?: string; details?: { code?: string } } | null;
  const code = e?.code || e?.details?.code;
  if (code === 'functions/not-found') {
    return 'This sign-in link is invalid or has expired. Start a new sign-in.';
  }
  if (code === 'functions/failed-precondition') {
    return e?.message || 'This sign-in link has already been used. Start a new sign-in.';
  }
  if (code === 'functions/invalid-argument') {
    // Deliberately NOT surfacing e.message here. The CF's invalid-argument
    // messages on this path describe malformed input, not anything the user
    // can act on — this is where "clientSessionId required." used to reach
    // real users who tapped the link from an iOS mail client.
    return 'This sign-in link is invalid or incomplete. Start a new sign-in.';
  }
  if (code === 'functions/unavailable' || code === 'functions/internal') {
    return 'Server unavailable. Try again in a moment.';
  }
  return e?.message || 'Sign-in failed. Try again.';
}

export function AuthVerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  // Guard against React 18 dev-mode double-invocation of useEffect — without
  // this the CF would be called twice on mount, and the second call would
  // hit "already used" because the first one consumed the token.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const token = searchParams.get('token')?.trim() || '';
    if (!token) {
      setErrorMessage('Sign-in link is missing the token. Start a new sign-in.');
      setStatus('error');
      return;
    }

    // Per-browser session id set by LandingPage when the email was requested.
    // Empty/missing reliably resolves to cross-device on the CF side.
    const clientSessionId = localStorage.getItem('email_login_session_id') || '';

    (async () => {
      try {
        const verifyFn = httpsCallable<
          { token: string; clientSessionId: string },
          VerifyResponse
        >(functions, 'verifyEmailLoginToken');

        const result = await verifyFn({ token, clientSessionId });
        const data = result.data;

        if (data.matchedDevice === false) {
          // Landing here in the browser that DID start the sign-in means its
          // stored session id is gone or rotated — so any resumable attempt
          // it kept is stale, and resuming it would only feed the user's code
          // to a token bound to an id we no longer have. Drop it so the
          // "Sign in on this device" button starts genuinely clean. (In the
          // usual case — a mail app opening the link in a different storage
          // jar — there's nothing here to clear.)
          clearPendingEmailLogin();
          setMaskedEmail(data.maskedEmail);
          setStatus('cross-device');
          return;
        }

        // Signed in via the link — the code screen has nothing left to resume.
        clearPendingEmailLogin();

        // matchedDevice === true: sign in + finalize.
        const { customToken, user } = data;
        await signInWithCustomToken(auth, customToken);

        // Mirror LandingPage.finalizeSignIn — without the selectedUser state
        // we route purely by role from the CF response. Wave 2: identity
        // keys via lib/session.
        // The CF returns the raw Firestore role ('Admin', 'Tech', …) — map
        // it to a session role AuthGuard accepts.
        const role = sessionRoleForUserRole(user.role);
        setSessionIdentity({
          userId: user.id,
          userName: user.name,
          tenantId: user.tenantId,
          role: user.dualAccess
            ? 'office'
            : role === 'guest_tech'
              ? 'tech'
              : role,
          ...(!user.dualAccess && (role === 'tech' || role === 'guest_tech')
            ? { techId: user.id, techName: user.name }
            : {}),
        });
        localStorage.setItem('last_login', new Date().toISOString());
        localStorage.setItem('session_persistent', 'true');
        setSessionExpiry();

        if (user.dualAccess || role === 'tech' || role === 'guest_tech') {
          // Dual-access users go to /home from the magic-link path (no
          // loginContext signal available). Tech-only users go to /tech.
          if (user.dualAccess) {
            navigate('/home', { replace: true });
          } else {
            setAuthExpiry();
            navigate('/tech', {
              replace: true,
              state: { techId: user.id, techName: user.name },
            });
          }
        } else if (role === 'office' || role === 'sales' || role === 'ops') {
          navigate('/home', { replace: true });
        } else if (role === 'provisioning') {
          navigate('/home/ops', { replace: true });
        } else {
          // Unknown role — land on /home and let the user navigate from there.
          navigate('/home', { replace: true });
        }
      } catch (err: unknown) {
        console.error('[AuthVerifyPage] verifyEmailLoginToken failed:', err);
        // Deliberately NOT clearing the pending attempt here. A dead link
        // (expired, already used, malformed) says nothing about whether this
        // browser has a live code screen waiting — the common shape is an
        // old email tapped while a fresh code is still good. Destroying that
        // would cost the user a whole extra round-trip; "Start a new
        // sign-in" resuming their live attempt is the friendlier miss.
        setErrorMessage(mapVerifyError(err));
        setStatus('error');
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="force-dark min-h-screen bg-brand-dark flex items-center justify-center p-6 text-white">
      <div className="w-full max-w-md rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-8 shadow-2xl">
        {status === 'verifying' && (
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
              <Loader2 className="w-6 h-6 animate-spin text-brand-secondary" />
            </div>
            <h1 className="text-xl font-black mb-1">Signing you in...</h1>
            <p className="text-sm text-white/60">Verifying your sign-in link.</p>
          </div>
        )}

        {status === 'cross-device' && (
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/15 flex items-center justify-center mb-4">
              <ShieldCheck className="w-6 h-6 text-amber-400" />
            </div>
            <h1 className="text-xl font-black mb-2">Open it where you started</h1>
            <p className="text-sm text-white/70 mb-3">
              This sign-in link is for{' '}
              <span className="font-mono text-white">{maskedEmail}</span>.
            </p>
            {/* On iOS this screen is the COMMON case, not the exotic one:
                Gmail/Outlook open links in their own in-app browser, and
                Apple Mail hands off to the default browser — neither shares
                storage with the home-screen PWA the user started in. So the
                copy leads with "start fresh here", which always works, and
                mentions the original-tab route second. */}
            <p className="text-sm text-white/60 mb-6">
              For security, links only work in the browser that asked for
              them — and phone mail apps usually open links somewhere else.
              Tap below to sign in right here, or switch back to the tab where
              you started and type the 6-digit code from the email.
            </p>
            <Link
              to="/"
              className="w-full py-3 rounded-2xl bg-brand-secondary text-brand-dark font-bold text-sm text-center hover:brightness-110 transition-all"
            >
              Sign in on this device
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-2xl bg-red-500/15 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <h1 className="text-xl font-black mb-2">Sign-in failed</h1>
            <p className="text-sm text-white/70 mb-6">{errorMessage}</p>
            <Link
              to="/"
              className="w-full py-3 rounded-2xl bg-brand-secondary text-brand-dark font-bold text-sm text-center hover:brightness-110 transition-all"
            >
              Start a new sign-in
            </Link>
          </div>
        )}

        {/* Branding strip */}
        <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-center gap-2 text-xs text-white/40">
          <MailCheck className="w-3.5 h-3.5" />
          <span>Email sign-in</span>
        </div>
      </div>
    </div>
  );
}
